import React from "react"
import { useTranslation } from "react-i18next"

import { setReelRunApplicationTypeAsync } from "@/base/farmhq-api"
import { useRequestHelpers } from "@/hooks/useRequestHelpers"
import { useToasts } from "@/hooks/useToasts"
import { AlertCard, Box, Radio } from "@/theme"
import { ActionButtons } from "./ActionButtons"

import type { ApplicationType } from "@/base/sensor-configurations"

interface SetReelRunApplicationTypeFormProps {
  currentValue: ApplicationType | null | undefined
  reelRunId: number
  onClose: () => void
}

/**
 * Lets the user override what an individual reel run applied
 * (water/manure/other) when it differs from the reel configuration's
 * application type.
 */
export function SetReelRunApplicationTypeForm({
  currentValue,
  reelRunId,
  onClose,
}: SetReelRunApplicationTypeFormProps): React.JSX.Element {
  const { t } = useTranslation("statusMap")
  const { t: tSensorFields } = useTranslation("sensorFields")

  const [selectedValue, setSelectedValue] = React.useState<ApplicationType>(
    currentValue ?? "water",
  )

  const toasts = useToasts()
  const { dispatch, handleError, isLoading } = useRequestHelpers(
    "SetReelRunApplicationType",
  )

  const onSubmit = () => {
    dispatch(
      setReelRunApplicationTypeAsync({
        reelRunId,
        value: selectedValue,
      }),
    )
      .unwrap()
      .then(() => onClose())
      .then(() => toasts.success())
      .catch((error) =>
        handleError(error, {
          toastMessage: "default",
        }),
      )
  }

  return (
    <React.Fragment>
      <AlertCard
        bodyText={t("setApplicationType.instructions")}
        severity={"info"}
      />
      <Box my={"$3"}>
        <Radio
          orientation={"horizontal"}
          selectedValue={selectedValue}
          textTransform={"capitalize"}
          options={[
            {
              label: tSensorFields("applicationType.values.water"),
              value: "water",
            },
            {
              label: tSensorFields("applicationType.values.manure"),
              value: "manure",
            },
            {
              label: tSensorFields("applicationType.values.other"),
              value: "other",
            },
          ]}
          onChange={(value) => setSelectedValue(value as ApplicationType)}
        />
      </Box>
      <ActionButtons
        isLoading={isLoading}
        orientation={"horizontal"}
        onPressCancel={onClose}
        onPressSubmit={onSubmit}
      />
    </React.Fragment>
  )
}
